<template>
  <div class="form-group">
    <div class="argon-phone-input-wrapper">
      <div class="phone-prefix-section" @click="toggleCountryDropdown">

        <div class="country-code">
          <span class="country-flag" v-if="selectedCountry">{{ selectedCountry.flag }}</span>
          <span class="prefix-text">+{{ countryCode }}</span>
          <i class="bi bi-chevron-down dropdown-arrow" :class="{ 'rotated': showDropdown }"></i>
        </div>

        <!-- Dropdown des pays -->
        <div v-if="showDropdown" class="country-dropdown" @click.stop>
          <div class="dropdown-search">
            <input type="text" v-model="searchQuery" placeholder="Rechercher un pays..." class="search-input"
              @click.stop />
          </div>
          <div class="dropdown-list">
            <div v-for="country in filteredCountries" :key="country.code" class="dropdown-item"
              :class="{ 'selected': country.code === countryCode }" @click="selectCountry(country)">
              <span class="country-flag">{{ country.flag }}</span>
              <span class="country-name">{{ country.name }}</span>
              <span class="country-code">+{{ country.code }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="phone-separator"></div>

      <div class="phone-input-section">
      <input
        ref="phoneInput"
        type="number"
        class="form-control phone-number-input"
        :class="getClasses(size, valid)"
        :name="name"
        :id="id"
        :value="inputValue"
        :placeholder="placeholder"
        :required="isRequired"
        :maxlength="maxLength"
        @input="handleInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="onFocus"
        @blur="onBlur"
        autocomplete="off"
        spellcheck="false"
      />
      </div>
    </div>

    <!-- Overlay pour fermer le dropdown -->
    <div v-if="showDropdown" class="dropdown-overlay" @click="closeDropdown"></div>
  </div>
</template>

<script>
import countryCodes from '@/assets/country-codes.json'

export default {
  name: 'argon-phone-input',
  props: {
    size: {
      type: String,
      default: 'lg'
    },
    valid: {
      type: Boolean,
      default: false
    },
    name: String,
    id: String,
    modelValue: String,
    placeholder: {
      type: String,
      default: '658959632'
    },
    type: {
      type: String,
      default: 'tel'
    },
    isRequired: Boolean,
    countryCode: {
      type: String,
      default: '237'
    },
    maxLength: {
      type: Number,
      default: 9
    }
  },
  emits: ["update:modelValue", "focus", "blur", "country-changed"],
  data() {
    return {
      isFocused: false,
      showDropdown: false,
      searchQuery: '',
      countries: countryCodes,
      selectedCountry: null,
      inputValue: ''
    }
  },
  computed: {
    filteredCountries() {
      if (!this.searchQuery) {
        return this.countries
      }
      return this.countries.filter(country =>
        country.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        country.code.includes(this.searchQuery)
      )
    }
  },
  mounted() {
    this.initializeSelectedCountry()
    this.clearInputValue()
    document.addEventListener('click', this.handleClickOutside)
    this.focus() // Set focus on input automatically on component load
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    modelValue(newVal) {
      // Ne pas remplir automatiquement avec modelValue au rechargement
      if (newVal && this.isFocused) {
        this.inputValue = this.sanitizeInput(newVal);
      }
    },
    countryCode: {
      handler() {
        this.initializeSelectedCountry()
      },
      immediate: true
    }
  },
  methods: {
    initializeSelectedCountry() {
      this.selectedCountry = this.countries.find(country => country.code === this.countryCode) || this.countries[0]
    },

    clearInputValue() {
      // Vider complètement le champ au rechargement
      this.inputValue = '';
      this.$emit('update:modelValue', '');
    },

    sanitizeInput(value) {
      // Ne garder que les chiffres
      return value.toString().replace(/\D/g, '');
    },

    toggleCountryDropdown() {
      this.showDropdown = !this.showDropdown
      if (this.showDropdown) {
        this.searchQuery = ''
      }
    },

    closeDropdown() {
      this.showDropdown = false
      this.searchQuery = ''
    },

    selectCountry(country) {
      this.selectedCountry = country
      this.showDropdown = false
      this.searchQuery = ''
      this.$emit('country-changed', {
        code: country.code,
        fullCode: `+${country.code}`,
        country: country
      })
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown()
      }
    },

    getClasses(size, valid) {
      let sizeValue = size ? `form-control-${size}` : '';
      let isValidValue = valid ? 'is-valid' : '';
      return `${sizeValue} ${isValidValue}`.trim();
    },

    handleInput(event) {
      const value = event.target.value;
      const sanitizedValue = this.sanitizeInput(value);
      const limitedValue = sanitizedValue.slice(0, this.maxLength);

      // Mettre à jour la valeur dans l'input
      event.target.value = limitedValue;
      this.inputValue = limitedValue;
      this.$emit('update:modelValue', limitedValue);
    },

    handleKeydown(event) {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];

      // Permettre les touches de contrôle
      if (allowedKeys.includes(event.key)) {
        return;
      }

      // Permettre Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
        return;
      }

      // Ne permettre que les chiffres (0-9)
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      // Vérifier la longueur maximale
      if (this.inputValue.length >= this.maxLength) {
        event.preventDefault();
      }
    },

    handlePaste(event) {
      event.preventDefault();
      
      // Récupérer le contenu du presse-papiers
      const paste = (event.clipboardData || window.clipboardData).getData('text');
      
      // Ne garder que les chiffres
      const sanitizedPaste = this.sanitizeInput(paste);
      
      // Limiter à la longueur maximale
      const limitedPaste = sanitizedPaste.slice(0, this.maxLength);
      
      // Mettre à jour la valeur
      this.inputValue = limitedPaste;
      event.target.value = limitedPaste;
      this.$emit('update:modelValue', limitedPaste);
    },

    onFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },

    onBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },

    focus() {
      this.$refs.phoneInput.focus();
    },
    onInput(event) {
      this.$emit('update:modelValue', event.target.value); // Pour v-model
      this.$emit('phone-input', event.target.value); // Événement personnalisé
    }
  }
}
</script>

<style scoped>
.argon-phone-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
  background: #fff;
  transition: all 0.15s ease;
  position: relative;
  overflow: visible;
}

.argon-phone-input-wrapper:focus-within {
  border-color: #5e72e4;
  box-shadow: 0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02);
}

.phone-prefix-section {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-right: 1px solid #dee2e6;
  padding: 0 8px;
  min-width: 100px;
      height: 40.39px;
  cursor: pointer;
  position: relative;
  right: -3px;
  transition: background 0.2s ease;
}

.phone-prefix-section:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
}

.phone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5e72e4;
  margin-right: 6px;
}

.phone-icon i {
  font-size: 15px;
}

.country-code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.country-flag {
  font-size: 16px;
}

.prefix-text {
  font-weight: 600;
  color: #2dce89;
  font-size: 15px;
  white-space: nowrap;
}

.dropdown-arrow {
  font-size: 12px;
  color: #6c757d;
  transition: transform 0.2s ease;
  margin-left: 4px;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.country-dropdown {
  position: absolute;
  top: 100%;
  left: -1px;
  right: -1px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
  width: 200px;
}

.dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #dee2e6;
}

.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #5e72e4;
}

.dropdown-list {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 8px;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.selected {
  background-color: #e3f2fd;
  color: #1976d2;
}

.dropdown-item .country-flag {
  font-size: 16px;
  min-width: 20px;
}

.dropdown-item .country-name {
  flex: 1;
  font-size: 14px;
}

.dropdown-item .country-code {
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

.phone-separator {
  width: 1px;
  height: 60%;
  background: #dee2e6;
  margin: 0 2px;
}

.phone-input-section {
  flex: 1;
  display: flex;
  align-items: center;
}

.phone-number-input {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: #344767;
  width: 100%;
  letter-spacing: 0.5px;
  height: 48.39px;
}

.phone-number-input:focus {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.argon-phone-input-wrapper {
  height: 48.39px;
}

.phone-number-input::placeholder {
  color: #adb5bd;
  opacity: 1;
}

/* Tailles spécifiques pour correspondre aux autres inputs */
.form-control-lg.phone-number-input {
  padding: 1rem 1.25rem !important;
  font-size: 1rem;
  line-height: 1.5;
}

.form-control-sm.phone-number-input {
  padding: 0.5rem 0.75rem !important;
  font-size: 0.75rem;
  line-height: 1.5;
}

/* États de validation */
.argon-phone-input-wrapper.is-valid {
  border-color: #2dce89;
}

.argon-phone-input-wrapper.is-invalid {
  border-color: #dc3545;
}

.is-valid .phone-prefix-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e8f5e8 100%);
}

.is-invalid .phone-prefix-section {
  background: linear-gradient(135deg, #fdf2f2 0%, #fef0f0 100%);
}

/* Responsive */
@media (max-width: 575.98px) {
  .phone-prefix-section {
    min-width: 90px;
    padding: 0 6px;
  }

  .phone-icon i {
    font-size: 15px !important;
  }

  .prefix-text {
    font-size: 15px !important;
  }

  .phone-number-input {
    font-size: 0.8rem !important;
  }

  .country-dropdown {
    max-height: 250px;
  }
}

@media (max-width: 767.98px) {
  .phone-prefix-section {
    min-width: 95px;
  }

  .form-control-lg.phone-number-input {
    padding: 0.875rem 1rem !important;
    font-size: 0.9rem;
  }
}

/* Animation au focus */
.argon-phone-input-wrapper:focus-within .phone-prefix-section {
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
}

.argon-phone-input-wrapper:focus-within .prefix-text {
  color: #5e72e4;
}

.argon-phone-input-wrapper:focus-within .phone-icon {
  color: #5e72e4;
}

/* Masquer les flèches des inputs number sur Chrome/Safari */
.phone-number-input::-webkit-outer-spin-button,
.phone-number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Masquer les flèches des inputs number sur Firefox */
.phone-number-input[type=number] {
  -moz-appearance: textfield;
}

/* Scrollbar personnalisée pour la liste des pays */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
